<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import ProductTabsReviewsPhotoSlider from './product-tabs-reviews-photo-slider.vue';
import { IconRating } from '@/features/ui-kit/icons';
import type { Feedback } from '@/shared/apollo-api/__generated__/types';
import { FeedbackFilterType, FeedbackSortType } from '@/shared/apollo-api/__generated__/types';
import { useFeedback } from '@/features/product-page/business/useFeedback';
import ProductTabsReviewsFilter from './product-tabs-reviews-filter.vue';
import ProductTabsReviewsItem from './product-tabs-reviews-item.vue';
import { FeatureFlags } from '@/features/feature-flags';
import { Plural } from '@/shared/helpers';

// Get feedback state and methods
const {
  feedback,
  feedbackLoading,
  hasMoreFeedback,
  feedbackQuantity,
  externalRating,
  rating,
  loadFeedback,
  loadMoreFeedback,
  loadMoreFeedbackWithPhotos,
  resetFeedback,
  currentSortValue,
  feedbackWithPhotos,
  feedbackWithPhotosLoading,
  hasMoreFeedbackWithPhotos,
} = useFeedback();

// import { PageType } from '@mm-frontend/frontend-bunseki';
// import { clickstreamTracker } from '@/shared/clickstream';
// import { EUiPropertiesAction, EUiPropertiesSpaceType } from '@mm-frontend/clickstream-client-js';

const showFilterCheckbox = computed(() => {
  let result = false;

  feedback.value.forEach((feed: Feedback) => {
    if (feed.photos?.length) {
      result = true;
    }
  });
  return result;
});

// Форматирование рейтинга до десятых
const formattedRating = computed(() => {
  return rating.value ? Number(rating.value).toFixed(1) : '0.0';
});

// Форматирование количества отзывов с правильным склонением
const formattedReviewsText = computed(() => {
  return Plural.with(feedbackQuantity.value, 'отзыв|отзыва|отзывов');
});

function filtersChanged(newFilters: FeedbackFilterType[]) {
  loadFeedback(0, 10, newFilters, FeedbackSortType.Relevance);
}

function sortChanged(sort: FeedbackSortType) {
  loadFeedback(0, 10, [], sort);
}

function eventShow(feed: Feedback) {
  console.log('Analytics: FEEDBACK_IMPRESSION', {
    space_name: 'Product',
    space_type: 'PAGE',
    space_id: String(feedbackQuantity.value),
    action: 'SHOW',
    product_id: feedbackQuantity.value,
    feedback_id: feed.id,
    rating: feed.rating,
    is_edited: false,
    is_anonymous: feed.anonymous,
    has_text: Boolean(feed.content),
    has_seller_response: Boolean(feed.reply),
    has_media: Boolean(feed.photos),
    page_type: 'PRODUCT',
  });
}

onMounted(async () => {
  await FeatureFlags.NEW_RATES().then((isNewRates: boolean) => {
    if (isNewRates) {
      loadFeedback(0, 10, [], FeedbackSortType.DateDesc);
    } else {
      loadFeedback(0, 10, [], FeedbackSortType.Relevance);
    }
  });
});
</script>

<template>
  <div v-if="feedbackQuantity > 0 && !externalRating" class="reviews-header">
    <div class="rating-section">
      <IconRating :size="20" color="rating-accented" />
      <span class="heading">{{ formattedRating }}</span>
      <span class="feedback-quantity">{{ formattedReviewsText }}</span>
    </div>
    <ProductTabsReviewsFilter
      :sort="currentSortValue"
      :filters="[]"
      @filtersChanged="filtersChanged"
      @sortChanged="sortChanged"
      :showCheckbox="showFilterCheckbox"
    />
    <product-tabs-reviews-photo-slider
      v-if="feedbackWithPhotos.length"
      :feedbackWithPhotos="feedbackWithPhotos"
      :feedbackWithPhotosLoading="feedbackWithPhotosLoading"
      @loadMoreFeedbackWithPhotos="loadMoreFeedbackWithPhotos"
    />
    <div class="feedback-wrap">
      <div class="feedback-item" v-for="feed in feedback" :key="feed.id">
        <ProductTabsReviewsItem :feedback="feed" @event-show-review="eventShow(feed)" />
      </div>
    </div>
    <button
      :disabled="feedbackLoading"
      v-if="hasMoreFeedback"
      @click="() => loadMoreFeedback()"
      class="more"
    >
      Показать ещё
    </button>
  </div>
  <div v-else>
    <span class="heading">Отзывов пока нет</span>
    <p class="text">Оформите заказ и поделитесь своим мнением о товаре</p>
  </div>
</template>

<style scoped lang="stylus">
.reviews-header
  margin-top 40px
  margin-bottom 24px

.rating-section
  display flex
  align-items center

.heading
  margin-left 8px
  margin-right 20px
  color: var(--gray-gray-dk-40, #141415);
  font-family: Inter;
  font-size: 22px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px; /* 127.273% */
  letter-spacing: -0.22px;

.text
  color: var(--archive-gray-gray-dk-40, #141415);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.07px;

.feedback-wrap
  padding-bottom 20px
  display flex
  flex-direction column
  gap 48px

.feedback-quantity
  color: var(--gray-60, #8B8E99);
  font-family: Inter;
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px; /* 127.273% */
  letter-spacing: -0.22px;

.more
  border-radius: 12px;
  background: var(--gray-98, #F5F6FA);
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  width 100%
  padding 16px 24px

  color: var(--gray-gray-dk-40, #141415);
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  margin-top 24px
  cursor pointer
</style>
